import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import { uploadFile } from '../../../utils/storageUtils';
import { compressImage } from '../../../utils/imageUtils';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import RepairCompletionModal from './RepairCompletionModal';

const RepairRequestPage = () => {
  const navigate = useNavigate();
  const { id: repairId } = useParams();
  const { equipment, requestRepair, updateRepair, requestRepairCompletion } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [problemDescription, setProblemDescription] = useState('');
  const [problemType, setProblemType] = useState('OTHER');
  const [urgency, setUrgency] = useState('NORMAL');
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [repairStatus, setRepairStatus] = useState(null);
  const [loadingRepair, setLoadingRepair] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedCompletionRepair, setSelectedCompletionRepair] = useState(null);
  const [currentRepair, setCurrentRepair] = useState(null);
  const [activeRepair, setActiveRepair] = useState(null); // For repairs found by equipment selection

  // Filter equipment (only damaged or available items that can be sent for repair)
  const filteredEquipment = equipment.filter(eq => {
    const matchesType = eq.type === EQUIPMENT_TYPES.LARGE_ITEM;
    const matchesStatus = eq.status === EQUIPMENT_STATUS.DAMAGED || 
                         eq.status === EQUIPMENT_STATUS.AVAILABLE ||
                         eq.status === EQUIPMENT_STATUS.REPAIR;
    const matchesSearch = !searchTerm || 
      eq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check plan-based limit
    const userPlan = currentUser?.plan || 'FREE';
    const maxImages = userPlan === 'PRO' ? 3 : 1;
    const remainingSlots = maxImages - images.length;

    if (remainingSlots <= 0) {
      alert(`ถึงขีดจำกัดการอัพโหลดรูปภาพแล้ว (${maxImages} รูป${userPlan === 'FREE' ? ' - สมัคร Pro Plan เพื่ออัพโหลดได้สูงสุด 3 รูป' : ''})`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      alert(`สามารถอัพโหลดได้อีก ${remainingSlots} รูปเท่านั้น (สูงสุด ${maxImages} รูป)`);
    }

    try {
      setUploadingImages(true);
      const uploadedUrls = [];

      for (const file of filesToUpload) {
        // Compress image
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8
        });

        // Upload to Firebase Storage
        const fileName = `equipment-repair-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const path = `equipment/${currentTeam.id}/${fileName}`;
        const url = await uploadFile(compressedFile, path);
        uploadedUrls.push(url);
      }

      setImages([...images, ...uploadedUrls]);
    } catch (err) {
      console.error('Error uploading images:', err);
      alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedEquipment) {
      alert('กรุณาเลือกอุปกรณ์ที่ต้องการส่งซ่อม');
      return;
    }

    if (!problemDescription.trim()) {
      alert('กรุณาอธิบายปัญหา');
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (isEditMode && repairId) {
        // Update existing repair
        const result = await updateRepair(repairId, {
          problemDescription: problemDescription.trim(),
          problemType,
          urgency,
          images
        });

        if (result.success) {
          alert('แก้ไขคำขอซ่อมสำเร็จ');
          navigate('/nurse/equipment/my-repairs');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } else {
        // Create new repair
        const result = await requestRepair(selectedEquipment.id, {
          problemDescription: problemDescription.trim(),
          problemType,
          urgency,
          images
        });

        if (result.success) {
          alert('ส่งคำขอซ่อมสำเร็จ');
          navigate('/nurse/equipment/my-repairs');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      }
    } catch (err) {
      console.error('Error submitting repair:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load repair data if in edit mode
  useEffect(() => {
    const loadRepair = async () => {
      if (repairId && currentTeam) {
        try {
          setLoadingRepair(true);
          const repairRef = doc(db, 'equipmentRepairs', repairId);
          const repairSnap = await getDoc(repairRef);
          
          if (repairSnap.exists()) {
            const repairData = repairSnap.data();
            setRepairStatus(repairData.status);
            
            // Only allow editing if status is PENDING
            if (repairData.status === 'PENDING') {
              setIsEditMode(true);
              setProblemDescription(repairData.problemDescription || '');
              setProblemType(repairData.problemType || 'OTHER');
              setUrgency(repairData.urgency || 'NORMAL');
              setImages(repairData.images || []);
              
              // Load equipment
              if (repairData.equipmentId) {
                const equipmentRef = doc(db, 'equipment', repairData.equipmentId);
                const equipmentSnap = await getDoc(equipmentRef);
                if (equipmentSnap.exists()) {
                  setSelectedEquipment({ id: equipmentSnap.id, ...equipmentSnap.data() });
                }
              }
            } else {
              // If status is APPROVED or IN_PROGRESS, allow completion request
              setIsEditMode(false);
              
              // Load equipment for display
              if (repairData.equipmentId) {
                const equipmentRef = doc(db, 'equipment', repairData.equipmentId);
                const equipmentSnap = await getDoc(equipmentRef);
                if (equipmentSnap.exists()) {
                  setSelectedEquipment({ id: equipmentSnap.id, ...equipmentSnap.data() });
                }
              }
              
              setCurrentRepair({
                id: repairId,
                equipmentId: repairData.equipmentId,
                equipmentName: repairData.equipmentName,
                status: repairData.status,
                completionRequestStatus: repairData.completionRequestStatus
              });
            }
          }
        } catch (err) {
          console.error('Error loading repair:', err);
        } finally {
          setLoadingRepair(false);
        }
      }
    };

    loadRepair();
  }, [repairId, currentTeam]);

  const getStatusBadge = (status) => {
    const badges = {
      [EQUIPMENT_STATUS.AVAILABLE]: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ พร้อมใช้' },
      [EQUIPMENT_STATUS.DAMAGED]: { bg: 'bg-red-100', text: 'text-red-800', label: '⚠️ เสียหาย' },
      [EQUIPMENT_STATUS.REPAIR]: { bg: 'bg-orange-100', text: 'text-orange-800', label: '🔧 อยู่ระหว่างส่งซ่อม' },
      [EQUIPMENT_STATUS.BORROWED]: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '📦 ถูกยืม' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">ส่งซ่อมอุปกรณ์</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Equipment List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">🔧 เลือกอุปกรณ์ที่ต้องการส่งซ่อม</h2>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="ค้นหาอุปกรณ์..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Equipment List */}
              {filteredEquipment.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🩺</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่พบอุปกรณ์</h3>
                  <p className="text-gray-600">ลองเปลี่ยนเงื่อนไขการค้นหาดู</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredEquipment.map(eq => {
                    const statusBadge = getStatusBadge(eq.status);
                    const isSelected = selectedEquipment?.id === eq.id;

                    return (
                      <div
                        key={eq.id}
                        onClick={() => !isEditMode && setSelectedEquipment(eq)}
                        className={`border rounded-lg p-4 transition ${
                          isEditMode 
                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'border-blue-500 bg-blue-50 cursor-pointer'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-800">{eq.name}</h3>
                              <span className={`px-2 py-1 text-xs rounded ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">รหัส: {eq.code}</p>
                            {eq.currentLocation && (
                              <p className="text-sm text-gray-500">📍 {eq.currentLocation}</p>
                            )}
                          </div>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!isEditMode) {
                                if (isSelected) {
                                  setSelectedEquipment(null);
                                  setActiveRepair(null);
                                } else {
                                  setSelectedEquipment(eq);
                                  // Check if this equipment has an active repair (APPROVED or IN_PROGRESS)
                                  if (currentTeam && eq.id) {
                                    try {
                                      const repairsQuery = query(
                                        collection(db, 'equipmentRepairs'),
                                        where('teamId', '==', currentTeam.id),
                                        where('equipmentId', '==', eq.id),
                                        where('status', 'in', ['APPROVED', 'IN_PROGRESS']),
                                        orderBy('createdAt', 'desc')
                                      );
                                      const snapshot = await getDocs(repairsQuery);
                                      if (!snapshot.empty) {
                                        const repairData = snapshot.docs[0].data();
                                        setActiveRepair({
                                          id: snapshot.docs[0].id,
                                          ...repairData
                                        });
                                      } else {
                                        setActiveRepair(null);
                                      }
                                    } catch (err) {
                                      console.error('Error checking active repair:', err);
                                      // Fallback: query without orderBy
                                      try {
                                        const fallbackQuery = query(
                                          collection(db, 'equipmentRepairs'),
                                          where('teamId', '==', currentTeam.id),
                                          where('equipmentId', '==', eq.id),
                                          where('status', 'in', ['APPROVED', 'IN_PROGRESS'])
                                        );
                                        const snapshot = await getDocs(fallbackQuery);
                                        if (!snapshot.empty) {
                                          const repairData = snapshot.docs[0].data();
                                          setActiveRepair({
                                            id: snapshot.docs[0].id,
                                            ...repairData
                                          });
                                        } else {
                                          setActiveRepair(null);
                                        }
                                      } catch (fallbackErr) {
                                        console.error('Error in fallback query:', fallbackErr);
                                        setActiveRepair(null);
                                      }
                                    }
                                  }
                                }
                              }
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${
                              isSelected
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {isSelected ? '✓ เลือกแล้ว' : 'เลือก'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Repair Form */}
          <div className="lg:col-span-1">
            {selectedEquipment ? (
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                <h2 className="text-lg font-bold text-gray-800 mb-4">📋 รายละเอียดปัญหา</h2>
                
                {isEditMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      ⚠️ คุณกำลังแก้ไขคำขอที่รอการอนุมัติ
                    </p>
                  </div>
                )}

                {/* Show status info if repair is APPROVED or IN_PROGRESS */}
                {repairId && (repairStatus === 'APPROVED' || repairStatus === 'IN_PROGRESS') && !isEditMode && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      ℹ️ คำขอซ่อมนี้ได้รับการอนุมัติแล้ว คุณสามารถส่งคำขอซ่อมเสร็จได้
                    </p>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🩺</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">{selectedEquipment.name}</h3>
                      <p className="text-sm text-gray-600">{selectedEquipment.code}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📝 อธิบายปัญหา <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={problemDescription}
                      onChange={(e) => setProblemDescription(e.target.value)}
                      rows={4}
                      placeholder="ระบุรายละเอียดปัญหา..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔧 ประเภทปัญหา
                    </label>
                    <select
                      value={problemType}
                      onChange={(e) => setProblemType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="NOT_WORKING">เครื่องไม่ทำงาน</option>
                      <option value="DAMAGED">เสียหาย</option>
                      <option value="MAINTENANCE">บำรุงรักษา</option>
                      <option value="OTHER">อื่นๆ</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ⚠️ ความเร่งด่วน
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="urgency"
                          value="NORMAL"
                          checked={urgency === 'NORMAL'}
                          onChange={(e) => setUrgency(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">ปกติ</span>
                      </label>
                      <label className="flex items-center p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="urgency"
                          value="URGENT"
                          checked={urgency === 'URGENT'}
                          onChange={(e) => setUrgency(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-red-600 font-medium">เร่งด่วน</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📷 รูปภาพ (ถ้ามี)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    {uploadingImages && (
                      <p className="text-sm text-blue-600 mt-2">กำลังอัปโหลด...</p>
                    )}
                    {images.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {images.map((url, index) => (
                          <div key={index} className="relative">
                            <img
                              src={url}
                              alt={`Problem ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {!isEditMode && (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !problemDescription.trim()}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอซ่อม'}
                    </button>
                  )}

                  {/* Show completion request button if repair status is APPROVED or IN_PROGRESS */}
                  {/* Case 1: Has repairId in URL (edit mode) */}
                  {repairId && (repairStatus === 'APPROVED' || repairStatus === 'IN_PROGRESS') && !isEditMode && selectedEquipment && (
                    <>
                      {currentRepair?.completionRequestStatus === 'PENDING' ? (
                        <div className="w-full px-4 py-3 bg-blue-100 text-blue-800 rounded-lg text-center font-semibold">
                          ⏳ รออนุมัติซ่อมเสร็จ
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowCompletionModal(true);
                          }}
                          className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                        >
                          ✅ ซ่อมเสร็จแล้ว
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Case 2: No repairId in URL but has activeRepair from equipment selection */}
                  {!repairId && activeRepair && (activeRepair.status === 'APPROVED' || activeRepair.status === 'IN_PROGRESS') && selectedEquipment && (
                    <>
                      {activeRepair.completionRequestStatus === 'PENDING' ? (
                        <div className="w-full px-4 py-3 bg-blue-100 text-blue-800 rounded-lg text-center font-semibold">
                          ⏳ รออนุมัติซ่อมเสร็จ
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowCompletionModal(true);
                          }}
                          className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                        >
                          ✅ ซ่อมเสร็จแล้ว
                        </button>
                      )}
                    </>
                  )}

                  {isEditMode && (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !problemDescription.trim()}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'กำลังส่ง...' : 'แก้ไขคำขอซ่อม'}
                    </button>
                  )}

                  {!repairId && (
                    <button
                      onClick={() => setSelectedEquipment(null)}
                      className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      ลบออก
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">🔧</div>
                  <p className="text-gray-600">เลือกอุปกรณ์ที่ต้องการส่งซ่อม</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Completion Request Modal */}
      <RepairCompletionModal
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setSelectedCompletionRepair(null);
        }}
        repair={selectedCompletionRepair}
        equipmentData={selectedEquipment}
        onSuccess={() => {
          if (activeRepair) {
            setActiveRepair({
              ...activeRepair,
              completionRequestStatus: 'PENDING'
            });
          }
          navigate('/nurse/equipment/my-repairs');
        }}
      />
    </div>
  );
};

export default RepairRequestPage;

